const IMG_DRONE_FLYING = "https://images.unsplash.com/photo-1524143986875-3b098d78b363?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const IMG_DRONE_SUNSET = "https://images.unsplash.com/photo-1533310266094-8898a03807dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const IMG_DRONE_FIELD = "https://images.unsplash.com/photo-1521405924368-64c5b84bec60?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

import { ukDroneLaws2026 } from "@/data/articles/ukDroneLaws2026";
import { whereCanIFly } from "@/data/articles/whereCanIFly";
import { droneRegistration } from "@/data/articles/droneRegistration";
import { privateProperty } from "@/data/articles/privateProperty";
import { gardenFlying } from "@/data/articles/gardenFlying";
import { beachFlying } from "@/data/articles/beachFlying";
import { nationalParks } from "@/data/articles/nationalParks";
import { nearAirports } from "@/data/articles/nearAirports";
import { djiMiniRules } from "@/data/articles/djiMiniRules";
import { djiAirRules } from "@/data/articles/djiAirRules";
import { djiMavicRules } from "@/data/articles/djiMavicRules";
import { remoteId } from "@/data/articles/remoteId";
import { nightFlying } from "@/data/articles/nightFlying";
import { overRoads } from "@/data/articles/overRoads";
import { nearRailways } from "@/data/articles/nearRailways";
import { criticalInfrastructure } from "@/data/articles/criticalInfrastructure";
import { howToCheck } from "@/data/articles/howToCheck";
import { droneInsurance } from "@/data/articles/droneInsurance";
import { photographyPrivacy } from "@/data/articles/photographyPrivacy";
import { finesPenalties } from "@/data/articles/finesPenalties";

const PILLAR_GUIDES = [
  ukDroneLaws2026,
  whereCanIFly,
  privateProperty,
  gardenFlying,
  beachFlying,
  nationalParks,
  nearAirports,
  djiMiniRules,
  djiAirRules,
  djiMavicRules,
  droneRegistration,
  remoteId,
  nightFlying,
  overRoads,
  nearRailways,
  criticalInfrastructure,
  howToCheck,
  droneInsurance,
  photographyPrivacy,
  finesPenalties,
];

export const GUIDES = [
  ...PILLAR_GUIDES,
  {
    slug: "a2-cofc-worth-it",
    title: "Is the A2 CofC Worth It? Cost, Benefits and How to Pass",
    description: "The A2 Certificate of Competence unlocks close-to-people flying for mid-size drones. Who actually needs it — and who's wasting £100.",
    category: "Qualifications",
    readTime: 6,
    updated: "June 2026",
    hero: IMG_DRONE_FLYING,
    intro: "The A2 CofC is the UK's most misunderstood drone qualification. For some pilots it transforms what's legally possible; for others it changes nothing at all. The difference comes down to what you fly.",
    sections: [
      {
        heading: "What the A2 CofC actually unlocks",
        paragraphs: [
          "With an A2 CofC, C2-class drones (under 4kg — think Mavic 3 with class marking) may fly just 30m from uninvolved people, or 5m in low-speed mode, instead of being exiled to A3's 150m-from-everything rule. For legacy drones between 250g and 2kg, it reduces the required distance to 50m.",
          "If you fly a sub-250g drone: the A2 CofC gives you nothing — you already have better privileges. Spend the money on ND filters instead.",
        ],
      },
      {
        heading: "Cost, format and difficulty",
        paragraphs: [
          "Expect around £100 with a CAA-recognised assessment entity. It's a remote, open-book, 30-question multiple-choice theory exam covering air law, meteorology, and operating procedures. Pass mark 75%. Most pilots pass first time after a weekend of study; the certificate lasts 5 years.",
        ],
        list: [
          "Prerequisite: valid Flyer ID (free)",
          "~2 hours of exam time, taken online from home",
          "No practical flight test — but you self-declare practical competence",
        ],
        callout: { type: "tip", text: "Doing paid work? Skip straight to the GVC (General VLOS Certificate, ~£1,000–1,500 with an Operational Authorisation) — it includes A2 privileges and is what most clients and councils ask for." },
      },
    ],
    faqs: [
      { q: "A2 CofC vs GVC — which do I need?", a: "A2 CofC for hobbyists with 250g–4kg drones who want to fly closer to people. GVC for anyone flying commercially under an Operational Authorisation, especially in congested areas. Many commercial pilots hold both." },
      { q: "Does the A2 CofC let me fly in FRZs?", a: "No. No qualification exempts you from Flight Restriction Zones — only permission from the relevant air traffic control does." },
    ],
  },
  {
    slug: "drone-laws-for-under-18s-uk",
    title: "UK Drone Laws for Under-18s: Kids, Teens and Family Flying",
    description: "There's no minimum age to fly a drone in the UK — but there is one for registration. How families can fly legally.",
    category: "Law",
    readTime: 5,
    updated: "June 2026",
    hero: IMG_DRONE_SUNSET,
    intro: "One of UK drone law's pleasant surprises: children can legally fly. The CAA deliberately set no minimum age for pilots — but wrapped it in a registration structure that requires an adult. Here's how it fits together.",
    sections: [
      {
        heading: "Who can fly vs who must register",
        paragraphs: [
          "Anyone of any age may take a Flyer ID test and fly a drone. The Operator ID — the £11.79/year registration that must be labelled on the aircraft — is different: you must be 18 to hold one. In a family, a parent registers as the operator and the child flies under that registration.",
        ],
        list: [
          "Flyer ID: no minimum age, free, 40 questions, pass mark 30",
          "Operator ID: 18+, covers every drone in the household",
          "The registered operator is legally responsible for ensuring every flight is safe and lawful",
        ],
        callout: { type: "warning", text: "The adult operator carries the legal liability. If a child pilot breaks airspace rules, enforcement lands on the registered operator." },
      },
      {
        heading: "Best practice for family flying",
        paragraphs: [
          "Sub-250g drones are ideal first aircraft — lightest rules, hardest to hurt anyone with. Take the Flyer ID test together even where it isn't mandatory; it's a genuinely good syllabus. Start in wide-open Class G countryside (use the Hot Spots on our map), keep below tree height at first, and make the pre-flight airspace check part of the ritual.",
        ],
      },
    ],
    faqs: [
      { q: "Can my 14-year-old fly a DJI Mini alone?", a: "Legally yes, provided a parent or guardian holds the Operator ID, the drone is labelled with it, and the flight follows all A1 rules. The operator remains responsible even when not present, so supervision is strongly advised." },
      { q: "Do toy drones need registration?", a: "Drones under 250g without a camera, or those classed as toys, need no registration at all. A camera (that isn't on a toy) triggers the Operator ID requirement regardless of weight." },
    ],
  },
  {
    slug: "commercial-drone-work-uk",
    title: "How to Start Commercial Drone Work in the UK",
    description: "GVC, insurance, pricing and the paperwork clients actually ask for — a realistic roadmap from hobbyist to paid pilot.",
    category: "Business",
    readTime: 8,
    updated: "June 2026",
    hero: IMG_DRONE_FIELD,
    intro: "There's no such thing as a UK 'commercial drone licence' any more — the old PfCO distinction died in 2021. What matters now is category of operation, not whether money changes hands. That said, real clients want real paperwork. Here's the stack.",
    sections: [
      {
        heading: "The qualification ladder",
        paragraphs: [
          "Plenty of paid work is legal in the Open category with nothing but registration — a sub-250g drone filming a farm, for instance. The moment you need to fly a heavier drone near people or in congested areas, you need the Specific category: a GVC (General VLOS Certificate) plus an Operational Authorisation from the CAA.",
        ],
        list: [
          "Step 1 — Flyer ID + Operator ID: the legal floor (£11.79/year)",
          "Step 2 — A2 CofC (~£100): mid-size drones closer to people",
          "Step 3 — GVC (~£1,000–1,500 incl. training): the professional standard, renewable Operational Authorisation ~£253/year to the CAA",
          "Step 4 — OSC (Operating Safety Case): bespoke permissions for BVLOS, sub-30m urban work — serious money, serious operations",
        ],
      },
      {
        heading: "Insurance and what clients demand",
        paragraphs: [
          "Commercial flights legally require EC 785/2004 compliant insurance — typically £300–600/year for £1–5m public liability from specialist providers. In practice clients (and especially councils and film productions) ask for: your Operational Authorisation number, proof of insurance, a RAMS document (risk assessment and method statement) per job, and flight logs.",
          "On pricing: UK day rates for a solo GVC pilot run roughly £350–700 for survey/inspection work, more with deliverables like orthomosaics or thermal reports. Under-pricing against hobbyists is the classic beginner mistake — the paperwork IS the product.",
        ],
        callout: { type: "tip", text: "Niche beats generalist: roof inspections, match-day football filming, solar farm thermography — pick one vertical, learn its jargon, and you'll out-earn 'drone photographer' every time." },
      },
    ],
    faqs: [
      { q: "Can I earn money with just a sub-250g drone?", a: "Yes — A1 privileges apply regardless of payment. You still need EC 785/2004 insurance for commercial flights and should carry public liability documentation clients can verify." },
      { q: "How long does the GVC take?", a: "Most training providers run a 2–3 day course (online theory + practical assessment). Adding the CAA's Operational Authorisation processing, allow 4–8 weeks from starting the course to being fully operational." },
    ],
  },
];

export const getGuideBySlug = (slug) => GUIDES.find((g) => g.slug === slug);
